import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../../../../hooks/useLagoon';
import { Option } from '../../../../../models/ProviderSearch';
import { adobeLinkTrackEvent } from '../../../../AdobeTagging/adobeLinkTrackEvent';
import { BhAoeItem, buildBhGroupedAoeData } from './groupedAoe.utils';

interface Props {
  model: string;
  defaultValue?: string[];
  isDisabled?: boolean;
  locationForAnalytics?: string;
  onOptionChange?: (data: { [key: string]: Option[] }) => void;
  options?: Option[]; // api data
  onApply?: (data: string[]) => void;
  submitBtnName?: string;
  disableSubmitBtn?: boolean;
  showApplyButton?: boolean;
}

export const GroupedAoeFilter = ({
  model,
  defaultValue = [],
  isDisabled = false,
  locationForAnalytics,
  options,
  onOptionChange = () => {},
  onApply,
  submitBtnName = 'Apply',
  disableSubmitBtn = false,
  showApplyButton = false,
}: Props) => {
  const { t } = useTranslation();
  const allConfigs = useLagoon('config');

  const [selectedValues, setSelectedValues] = useState<string[]>(
    defaultValue.map(String)
  );

  useEffect(() => {
    setSelectedValues(defaultValue.map(String));
  }, [JSON.stringify(defaultValue)]);

  const groupedAoeData = useMemo(
    () => buildBhGroupedAoeData(options || [], allConfigs),
    [options, allConfigs]
  );

  const allItems: BhAoeItem[] = groupedAoeData.flatMap((cat) => cat.items);
  /// explain in next cxall
  const handleChange = (
    newCategoryValues: string[],
    categoryItems: BhAoeItem[]
  ) => {
    const categoryItemValues = new Set(
      categoryItems.map((item) => item.value).filter(Boolean)
    );
    const keptFromOtherCategories = selectedValues.filter(
      (v) => !categoryItemValues.has(v)
    );
    const nextSelected = [...keptFromOtherCategories, ...newCategoryValues];

    if (isEqual(selectedValues, nextSelected)) return;

    // Adobe analytics
    const added = newCategoryValues.filter((v) => !selectedValues.includes(v));
    const removed = categoryItems
      .filter(
        (item) =>
          item.value &&
          selectedValues.includes(item.value) &&
          !newCategoryValues.includes(item.value)
      )
      .map((item) => item.value);

    if (added.length > 0) {
      const item = categoryItems.find((i) => i.value === added[0]);
      adobeLinkTrackEvent({
        name: `${item?.dnt_label}_${added[0]}:check`,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    } else if (removed.length > 0) {
      const item = categoryItems.find((i) => i.value === removed[0]);
      adobeLinkTrackEvent({
        name: `${item?.dnt_label}_${removed[0]}:uncheck`,
        location: locationForAnalytics,
        type: 'checkbox',
      });
    }

    setSelectedValues(nextSelected);
    onOptionChange({
      [model]: allItems
        .filter((item) => item.value && nextSelected.includes(item.value))
        .map((item) => ({
          label: item.label,
          value: item.value,
          dnt_label: item.dnt_label,
        })),
    });
  };
  //
  return (
    <Flex
      css={{
        'abyss-flex-root': {
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          width: '100%',
        },
      }}
      direction="row"
    >
      <Flex
        css={{
          'abyss-flex-root': {
            maxHeight: '400px',
            overflow: 'auto',
            overflowX: 'hidden',
            paddingLeft: '4px',
            paddingBottom: '4px',
            flexDirection: 'row',
            gap: '16px',
          },
        }}
        direction="row"
      >
        {groupedAoeData.map((category) => {
          const categorySelectedValues = selectedValues.filter((v) =>
            category.items.some((item) => item.value === v)
          );
          const label = category.category.split('.').pop()?.toLowerCase();
          return (
            <Flex
              css={{
                'abyss-flex-root': { flexDirection: 'column', gap: '4px' },
              }}
              direction="row"
              key={category.category}
            >
              <Text
                css={{
                  'abyss-text-root': {
                    fontWeight: '700',
                    fontSize: '14px !important',
                    color: '#323334',
                    paddingBottom: '4px',
                    lineHeight: '18px',
                  },
                }}
                data-testid={`bh-aoe-${label}-label`}
              >
                {t(category.category)}
              </Text>
              <CheckboxGroup
                css={{
                  'abyss-checkbox-group-label': { margin: '0' },
                  'abyss-checkbox-group-root': { border: 'none' },
                }}
                data-testid={`bh-aoe-${category.category
                  .replace(/ /g, '-')
                  .toLowerCase()}-group`}
                label=""
                onChange={(e) => handleChange(e, category.items)}
                validators={{ required: false }}
                value={categorySelectedValues}
              >
                {category.items.map((item) => (
                  <Checkbox
                    css={{
                      'abyss-checkbox-root': {
                        padding: '10px 0px 10px $xs',
                        margin: '0 !important',
                        alignItems: 'center !important',
                      },
                      'abyss-checkbox-label': {
                        fontSize: '16px',
                        color: isDisabled
                          ? '$gray5'
                          : selectedValues.includes(item.value)
                          ? '$gray8'
                          : '$gray7',
                        fontWeight:
                          !isDisabled && selectedValues.includes(item.value)
                            ? '$bold'
                            : '$medium',
                      },
                    }}
                    data-testid="checkbox-selection"
                    isDisabled={isDisabled || !item.value}
                    key={item.value || item.label}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </CheckboxGroup>
            </Flex>
          );
        })}
      </Flex>

      {showApplyButton && (
        <Flex
          css={{
            'abyss-flex-root': {
              justifyContent: 'flex-start',
              width: '100%',
            },
          }}
          direction="row"
        >
          <Button
            data-testid={`${model}-grouped-aoe-apply`}
            isDisabled={disableSubmitBtn}
            onClick={() => onApply?.(selectedValues)}
          >
            {submitBtnName}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
